import { queryOptions, useMutation, useQueryClient } from '@tanstack/react-query';
import { api, unwrap, type Schemas } from '@/lib/api/client';
import { auditKeys } from '@/features/audit/keys';

export type Source = Schemas['ScrapeSourceDto'];
/** Judged by the API from the source's complete runs; see `healthStatus` for the words. */
export type SourceHealth = Schemas['SourceHealthDto'];
/** Create and edit take the same fields; an edit replaces all of them. */
export type SourceInput = Schemas['CreateScrapeSourceRequest'];

export const sourceKeys = {
  all: ['sources'] as const,
  list: () => [...sourceKeys.all, 'list'] as const,
};

/** Every source, disabled ones too. The API has no single-source read, so detail uses this. */
export const sourcesQuery = () =>
  queryOptions({
    queryKey: sourceKeys.list(),
    queryFn: () => unwrap(api.GET('/api/scraping/sources')),
    staleTime: 60_000,
  });

/** "trka.rs" rather than the full URL, for dense lists. */
export function sourceLabel(source: Source | undefined): string | undefined {
  if (!source) return undefined;
  try {
    return new URL(source.url).host.replace(/^www\./, '');
  } catch {
    return source.name;
  }
}

export const toInput = (source: Source): SourceInput => ({
  name: source.name,
  url: source.url,
  requiresJsRendering: source.requiresJsRendering,
  countryHint: source.countryHint,
  notes: source.notes,
});

// Awaited, so a caller that moves on after the mutation finds the list already current.
function useRefreshSources() {
  const queryClient = useQueryClient();
  return () => queryClient.invalidateQueries({ queryKey: sourceKeys.all });
}

export function useCreateSource() {
  const refresh = useRefreshSources();
  return useMutation({
    mutationFn: (input: SourceInput) =>
      unwrap(api.POST('/api/scraping/sources', { body: input })).then((r) => r.sourceId),
    onSuccess: refresh,
  });
}

export function useUpdateSource(id: string) {
  const refresh = useRefreshSources();
  return useMutation({
    mutationFn: (input: SourceInput) =>
      unwrap(
        api.PUT('/api/scraping/sources/{sourceId}', {
          params: { path: { sourceId: id } },
          body: input,
        }),
      ),
    onSettled: refresh,
  });
}

export function useSetSourceEnabled(id: string) {
  const refresh = useRefreshSources();
  return useMutation({
    mutationFn: (enabled: boolean) =>
      unwrap(
        enabled
          ? api.POST('/api/scraping/sources/{sourceId}/enable', {
              params: { path: { sourceId: id } },
            })
          : api.POST('/api/scraping/sources/{sourceId}/disable', {
              params: { path: { sourceId: id } },
            }),
      ),
    onSettled: refresh,
  });
}

/** Pause stops the source running by hand or on schedule; its run in progress stops before its next page. */
export function useSetSourcePaused(id: string) {
  const refresh = useRefreshSources();
  return useMutation({
    mutationFn: ({ paused, note }: { paused: boolean; note?: string | null }) =>
      unwrap(
        paused
          ? api.POST('/api/scraping/sources/{sourceId}/pause', {
              params: { path: { sourceId: id } },
              body: { note: note ?? null },
            })
          : api.POST('/api/scraping/sources/{sourceId}/resume', {
              params: { path: { sourceId: id } },
            }),
      ),
    onSettled: refresh,
  });
}

/** Asks every page of the source to be read again on its next run. */
export function useRequestSourceReRead(id: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: () =>
      unwrap(
        api.POST('/api/scraping/sources/{sourceId}/re-read', {
          params: { path: { sourceId: id } },
        }),
      ),
    // Awaited, so the page swaps its region once the source carries the request.
    onSettled: () =>
      Promise.all([
        queryClient.invalidateQueries({ queryKey: sourceKeys.all }),
        queryClient.invalidateQueries({ queryKey: auditKeys.all }),
      ]),
  });
}
